import type { AuthProvider, DeploymentRegion, PaymentMethod, SiteCapabilities, SiteConfig, SiteEdition } from '../types/site'

export const SITE_EDITIONS: SiteEdition[] = ['ai', 'vip']
export const DEPLOYMENT_REGIONS: DeploymentRegion[] = ['global', 'cn']

export const SITE_CAPABILITY_PROFILES: Record<SiteEdition, SiteCapabilities> = {
  ai: {
    authProviders: ['google', 'linuxdo', 'twitter', 'facebook'],
    paymentMethods: ['stripe'],
    accountDomainMigration: false,
  },
  vip: {
    authProviders: ['qq', 'wechat'],
    paymentMethods: ['alipay', 'wechat'],
    // 企业端不使用 C 端登录/注册的跨站账号迁移引导。
    accountDomainMigration: false,
  },
}

export const PAYMENT_METHOD_CODE_MAP: Record<PaymentMethod, number> = {
  stripe: 1,
  paypal: 4,
  alipay: 5,
  wechat: 6,
}

export const PAYMENT_CODE_METHOD_MAP: Record<number, PaymentMethod> = {
  1: 'stripe',
  4: 'paypal',
  5: 'alipay',
  6: 'wechat',
}

export interface ResolveSiteConfigInput {
  siteEdition?: unknown
  deploymentRegion?: unknown
  counterpartSiteUrl?: unknown
}

export interface ResolveSiteConfigOptions {
  requireCounterpartSiteUrl?: boolean
}

const normalizeString = (value: unknown) => String(value || '').trim().toLowerCase()

function isHttpUrl(value: string) {
  try {
    const url = new URL(value)
    return ['http:', 'https:'].includes(url.protocol) && Boolean(url.hostname)
  } catch {
    return false
  }
}

export function isSiteEdition(value: unknown): value is SiteEdition {
  return SITE_EDITIONS.includes(normalizeString(value) as SiteEdition)
}

export function isDeploymentRegion(value: unknown): value is DeploymentRegion {
  return DEPLOYMENT_REGIONS.includes(normalizeString(value) as DeploymentRegion)
}

export function resolveSiteConfig(input: ResolveSiteConfigInput, options: ResolveSiteConfigOptions = {}): SiteConfig {
  const edition = normalizeString(input.siteEdition)
  const deploymentRegion = normalizeString(input.deploymentRegion)
  const counterpartSiteUrl = String(input.counterpartSiteUrl || '').trim()

  if (!isSiteEdition(edition)) {
    throw new Error(`Invalid NUXT_PUBLIC_SITE_EDITION: ${String(input.siteEdition || '')}`)
  }
  if (!isDeploymentRegion(deploymentRegion)) {
    throw new Error(`Invalid NUXT_PUBLIC_DEPLOYMENT_REGION: ${String(input.deploymentRegion || '')}`)
  }
  if (options.requireCounterpartSiteUrl && !counterpartSiteUrl) {
    throw new Error('NUXT_PUBLIC_COUNTERPART_SITE_URL is required')
  }
  if (counterpartSiteUrl && !isHttpUrl(counterpartSiteUrl)) {
    throw new Error(`Invalid NUXT_PUBLIC_COUNTERPART_SITE_URL: ${counterpartSiteUrl}`)
  }

  return {
    edition,
    deploymentRegion,
    counterpartSiteUrl,
    capabilities: SITE_CAPABILITY_PROFILES[edition],
  }
}

export function getAvailablePaymentMethods(paymentMethods: PaymentMethod[], currency?: string): PaymentMethod[] {
  const normalizedCurrency = normalizeString(currency)
  return paymentMethods.filter((method) => {
    if (method === 'paypal') {
      return normalizedCurrency !== 'cny' && normalizedCurrency !== 'krw'
    }
    return true
  })
}

export function getDefaultPaymentMethod(
  currentMethod: PaymentMethod | undefined,
  paymentMethods: PaymentMethod[],
  currency?: string,
): PaymentMethod {
  const availableMethods = getAvailablePaymentMethods(paymentMethods, currency)
  if (currentMethod && availableMethods.includes(currentMethod)) {
    return currentMethod
  }
  return availableMethods[0] || paymentMethods[0] || 'stripe'
}

export function getPaymentMethodByCode(code: number): PaymentMethod | undefined {
  return PAYMENT_CODE_METHOD_MAP[code]
}

export function getPaymentCodeByMethod(method: PaymentMethod): number {
  return PAYMENT_METHOD_CODE_MAP[method]
}

export function shouldOpenAccountDomainMigration(code: unknown, siteConfig: Pick<SiteConfig, 'capabilities'>): boolean {
  return String(code || '') === '10051' && siteConfig.capabilities.accountDomainMigration
}

export function createAccountDomainMigrationGate(siteConfig: Pick<SiteConfig, 'capabilities'>) {
  let hasTriggered = false

  return (code: unknown) => {
    if (hasTriggered || !shouldOpenAccountDomainMigration(code, siteConfig)) {
      return false
    }
    hasTriggered = true
    return true
  }
}

export function createVisitorRegionState(countryCode?: string) {
  const visitorCountryCode = String(countryCode || '').trim().toUpperCase()
  return {
    visitorCountryCode,
    isVisitorInChina: visitorCountryCode === 'CN',
  }
}
